// src/pages/BlogPostPage.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import api from "../api/axiosInstance";
import BlogPost from '../components/BlogPost';
import { BlogPost as BlogPostType } from '../types/blog';

const API_BASE_URL =
    process.env.NODE_ENV === 'development'
        ? 'http://localhost:8080'
        : 'https://api.sonabelanska.sk';

// Opravený styled komponent s explicitným typom props
const RelatedImage = styled.div<{ src?: string }>`
  width: 100%;
  height: 160px;
  background-image: url(${props => props.src || ''});
  background-size: cover;
  background-position: center;
  background-color: ${props => (props.src ? 'transparent' : '#f1f5f9')};
`;

const PlaceholderImage = styled.div`
    width: 100%;
    height: 160px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
    color: #cbd5e1;
`;

// Ostatné styly (nezmenené)
const PageContainer = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%);
    padding: 2rem 0;
    padding-top: 150px;
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const BackButton = styled(motion.button)`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    color: #4a5568;
    font-weight: 600;
    margin-bottom: 2rem;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.3s ease;

    &:hover {
        border-color: #990147;
        color: #990147;
        box-shadow: 0 4px 15px rgba(153, 1, 71, 0.1);
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4rem;
    color: #64748b;
    font-size: 1.1rem;
    flex-direction: column;
    gap: 1rem;
`;

const ErrorContainer = styled.div`
    text-align: center;
    padding: 3rem;
    color: #dc2626;
    background: #fef2f2;
    border-radius: 12px;
    margin: 2rem 0;
    border: 1px solid #fecaca;
`;

const RelatedPosts = styled.section`
    margin-top: 4rem;
    padding-top: 3rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const RelatedTitle = styled.h2`
    font-size: 2rem;
    font-weight: 800;
    color: #000000;
    margin-bottom: 2rem;
    text-align: center;

    @media (max-width: 768px) {
        font-size: 1.75rem;
    }
`;

const RelatedGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        max-width: 420px;
        margin: 0 auto;
    }
`;

const RelatedCard = styled(motion.article)`
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    }
`;

const RelatedContent = styled.div`
    padding: 1.25rem;
`;

const RelatedCategory = styled.span`
    display: inline-block;
    padding: 0.3rem 0.8rem;
    border-radius: 15px;
    font-size: 0.7rem;
    font-weight: 700;
    color: #ffffff;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    text-transform: uppercase;
    margin-bottom: 0.75rem;
`;

const RelatedPostTitle = styled.h3`
    font-size: 1.1rem;
    font-weight: 700;
    color: #000000;
    line-height: 1.4;
    margin-bottom: 0.5rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const RelatedMeta = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 0.8rem;
    color: #64748b;
    margin-top: 0.75rem;
`;

const BlogPostPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [post, setPost] = useState<BlogPostType | null>(null);
    const [relatedPosts, setRelatedPosts] = useState<BlogPostType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString('sk-SK', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // Načítanie hlavného článku
    useEffect(() => {
        const fetchPost = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const response = await api.get(`${API_BASE_URL}/api/posts/${id}`);
                setPost(response.data);
            } catch (err: any) {
                console.error('Chyba pri načítaní článku:', err);
                setError('Článok sa nepodarilo načítať.');
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [id]);

    // Načítanie súvisiacich článkov
    useEffect(() => {
        if (!post) return;

        const fetchRelated = async () => {
            try {
                const response = await api.get(`${API_BASE_URL}/api/posts`);
                const allPosts: BlogPostType[] = response.data;

                const related = allPosts
                    .filter(p => p.category.id === post.category.id && p.id !== post.id)
                    .slice(0, 3);

                setRelatedPosts(related);
            } catch (err) {
                console.error('Chyba pri načítaní súvisiacich článkov:', err);
            }
        };

        fetchRelated();
    }, [post]);

    if (loading) {
        return (
            <PageContainer>
                <Container>
                    <BackButton onClick={() => navigate('/blog')} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        ← Späť na blog
                    </BackButton>
                    <LoadingContainer>
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            style={{
                                width: 40,
                                height: 40,
                                border: '4px solid #e2e8f0',
                                borderTop: '4px solid #990147',
                                borderRadius: '50%'
                            }}
                        />
                        Načítavam článok...
                    </LoadingContainer>
                </Container>
            </PageContainer>
        );
    }

    if (error || !post) {
        return (
            <PageContainer>
                <Container>
                    <BackButton onClick={() => navigate('/blog')} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        ← Späť na blog
                    </BackButton>
                    <ErrorContainer>
                        <h3>Článok sa nenašiel</h3>
                        <p>{error || 'Požadovaný článok neexistuje alebo bol odstránený.'}</p>
                        <motion.button
                            onClick={() => navigate('/blog')}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            style={{
                                marginTop: '1rem',
                                padding: '0.75rem 1.5rem',
                                background: '#990147',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer'
                            }}
                        >
                            Späť na blog
                        </motion.button>
                    </ErrorContainer>
                </Container>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Container>
                <BackButton onClick={() => navigate('/blog')} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    ← Späť na blog
                </BackButton>

                {/* Priamy prenos dát – žiadna transformácia */}
                <BlogPost post={post} />

                {relatedPosts.length > 0 && (
                    <RelatedPosts>
                        <RelatedTitle>Súvisiace články</RelatedTitle>
                        <RelatedGrid>
                            {relatedPosts.map((related, index) => (
                                <RelatedCard
                                    key={related.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    onClick={() => navigate(`/blog/${related.id}`)}
                                >
                                    {related.featuredImage ? (
                                        <RelatedImage src={related.featuredImage} />
                                    ) : (
                                        <PlaceholderImage>Article</PlaceholderImage>
                                    )}
                                    <RelatedContent>
                                        <RelatedCategory>{related.category.name}</RelatedCategory>
                                        <RelatedPostTitle>{related.title}</RelatedPostTitle>
                                        <RelatedMeta>
                                            <span>{formatDate(related.createdAt)}</span>
                                            <span>{related.readTime || '5 min'}</span>
                                        </RelatedMeta>
                                    </RelatedContent>
                                </RelatedCard>
                            ))}
                        </RelatedGrid>
                    </RelatedPosts>
                )}
            </Container>
        </PageContainer>
    );
};

export default BlogPostPage;